import React, { FC } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'

import { RFFCustomSelectV2, Typography } from '@dg-shared'
import { TranslatedTextType } from '../../../../hooks/shared'
import { ProductPropV2Type } from '../../../../types/ApiV2'
import { FieldOptions } from '../../../../types/interface/form.interface'

const useClaimsSelectStyles = makeStyles()((theme: Theme) => ({
  label: {
    marginBottom: theme.spacing(0),
  },

  helper: {
    marginBottom: 0,
    marginTop: theme.spacing(2),
    color: theme.brand.colors.textError,
  },
}))

export interface ReviewClaimsSelectProps {
  label: TranslatedTextType
  placeholder: TranslatedTextType
  helperLines: TranslatedTextType[]
  options: ProductPropV2Type['values']
  value: string
  onChange(value: string): void
  testId?: string
}

const noop = (): void => undefined

// DFE-4063: Review Info select that forces an active "number of claims" choice (no default).
// Reuses the shared `RFFCustomSelectV2` driven by a controlled `input` from the Review
// useLogic hook (no react-final-form context needed). This thin wrapper only adds the two
// always-on regulatory helper lines, which the shared select does not render itself.
const ReviewClaimsSelect: FC<ReviewClaimsSelectProps> = ({
  label,
  placeholder,
  helperLines,
  options,
  value,
  onChange,
  testId = 'review-previous-claims',
}) => {
  const { classes } = useClaimsSelectStyles()

  return (
    <div data-testid={`${testId}-block`}>
      <RFFCustomSelectV2
        fieldName={testId}
        selectTestId={`select-${testId}`}
        customLabel={
          <Typography testId={`${testId}-label`} className={classes.label}>
            {label}
          </Typography>
        }
        // `RFFCustomSelectV2` renders from `fieldOptions` and reads each option's `name`/`value`
        // (its `FieldOptions` type is looser than the datasource shape the whole app feeds it).
        // `options` is a required-but-unused prop on the component, so pass an empty array.
        options={[]}
        fieldOptions={options as unknown as FieldOptions[]}
        defaultItem={{ value: '', text: placeholder as string }}
        input={{
          name: testId,
          value,
          onChange: (event) => onChange((event.target as HTMLInputElement).value),
          onBlur: noop,
          onFocus: noop,
        }}
        meta={{}}
      />
      {helperLines.map((line, idx) => (
        <Typography
          key={`${testId}-helper-${idx}`}
          testId={`${testId}-helper-${idx}`}
          size='small'
          color='textError'
          className={classes.helper}
        >
          {line}
        </Typography>
      ))}
    </div>
  )
}

export default ReviewClaimsSelect
